/**
 * TrialUpgradeGate
 *
 * Wraps premium feature pages. Behaviour:
 *   - Active trial           → render children + sticky trial banner
 *   - Active paid sub        → render children (no banner)
 *   - Grace period (7 days)  → render children + sticky payment-failure banner (limited mode)
 *   - Trial expired / no sub → render upgrade wall (blocks content)
 *
 * Usage:
 *   <TrialUpgradeGate>
 *     <DocumentsPage />
 *   </TrialUpgradeGate>
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Alert,
  AlertTitle,
  Button,
  Typography,
  Paper,
  Stack,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import StarIcon        from '@mui/icons-material/Star';
import WarningIcon     from '@mui/icons-material/Warning';
import { useAuth } from '../contexts/AuthContext';

// ─── trial banner ─────────────────────────────────────────────────────────────

const TrialBanner = ({ daysRemaining }) => {
  const navigate = useNavigate();
  return (
    <Alert
      severity={daysRemaining <= 3 ? 'warning' : 'info'}
      icon={<StarIcon fontSize="inherit" />}
      sx={{ borderRadius: 0, position: 'sticky', top: 0, zIndex: 1200 }}
      action={
        <Button
          size="small"
          color="inherit"
          variant="outlined"
          onClick={() => navigate('/subscription')}
          sx={{ whiteSpace: 'nowrap', mr: 1 }}
        >
          Upgrade Now
        </Button>
      }
    >
      <AlertTitle sx={{ mb: 0 }}>
        {daysRemaining <= 3
          ? `Trial expires in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} — upgrade to keep access`
          : `Free trial active — ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining`}
      </AlertTitle>
    </Alert>
  );
};

// ─── grace period banner ──────────────────────────────────────────────────────

const GraceBanner = ({ daysRemaining }) => {
  const navigate = useNavigate();
  return (
    <Alert
      severity="warning"
      icon={<WarningIcon fontSize="inherit" />}
      sx={{ borderRadius: 0, position: 'sticky', top: 0, zIndex: 1200 }}
      action={
        <Button
          size="small"
          color="inherit"
          variant="outlined"
          onClick={() => navigate('/subscription')}
          sx={{ whiteSpace: 'nowrap', mr: 1 }}
        >
          Fix Billing
        </Button>
      }
    >
      <AlertTitle sx={{ mb: 0 }}>
        Payment failed — {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} to resolve before access is suspended
      </AlertTitle>
    </Alert>
  );
};

// ─── upgrade wall ─────────────────────────────────────────────────────────────

const UpgradeWall = () => {
  const navigate = useNavigate();
  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" px={2}>
      <Paper elevation={3} sx={{ maxWidth: 480, width: '100%', p: 4, textAlign: 'center', borderRadius: 3 }}>
        <LockOutlinedIcon sx={{ fontSize: 56, color: 'text.secondary', mb: 1 }} />
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Upgrade to Access This Feature
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Your free trial has ended. Choose a plan to continue using all FamilyForLife
          features and keep your family connected.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/subscription')}
            startIcon={<StarIcon />}
          >
            View Plans
          </Button>
          <Button variant="outlined" size="large" onClick={() => navigate('/')}>
            Go to Home
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

// ─── gate ─────────────────────────────────────────────────────────────────────

const TrialUpgradeGate = ({ children }) => {
  const {
    isAccessGranted,
    isTrialActive,
    trialDaysRemaining,
    isInGracePeriod,
    graceDaysRemaining,
  } = useAuth();

  // Dev/staging: never block — show an info banner instead
  if (process.env.NODE_ENV !== 'production') {
    return (
      <>
        <Alert
          severity="info"
          icon={<StarIcon fontSize="inherit" />}
          sx={{ borderRadius: 0, position: 'sticky', top: 0, zIndex: 1200 }}
          action={
            <Button
              size="small"
              color="inherit"
              variant="outlined"
              onClick={() => window.location.assign('/subscription')}
              sx={{ whiteSpace: 'nowrap', mr: 1 }}
            >
              View Plans
            </Button>
          }
        >
          <AlertTitle sx={{ mb: 0 }}>Dev mode — subscription gate bypassed</AlertTitle>
        </Alert>
        {children}
      </>
    );
  }

  // Grace period — allow access but show prominent warning
  if (isInGracePeriod) {
    return (
      <>
        <GraceBanner daysRemaining={graceDaysRemaining} />
        {children}
      </>
    );
  }

  // Active paid subscription — no banner
  if (isAccessGranted && !isTrialActive) {
    return children;
  }

  // Active trial — show trial banner
  if (isTrialActive) {
    return (
      <>
        <TrialBanner daysRemaining={trialDaysRemaining} />
        {children}
      </>
    );
  }

  // No access — block with upgrade wall
  return <UpgradeWall />;
};

export default TrialUpgradeGate;
